import React from 'react';

import { INSIGHTS_ICONS } from '~/components/AiChat/SideBarDialog/utils';
import type { AiAction } from '~/components/AiChat/types';
import { useActionCard } from '~/components/ClaimPage/CliveApp/CliveInsights/hooks/useActionCard';
import { DismissWithReasonDialog } from '~/components/Clive/CliveDialog/DismissWithReasonDialog';
import mixpanel from '~/components/CmsMain/mixpanel';
import { Text } from '~/components/core/TextComponents';
import { CLIVE_MIXPANEL_EVENTS } from '~/pocs/mixpanel';
import cn from '~/Utils/cn';

interface ActionCardV2Props {
  action: AiAction;
  onActionUpdate: () => Promise<void>;
}

export const ActionCardV2: React.FC<ActionCardV2Props> = ({ action, onActionUpdate }) => {
  const {
    taskType,
    title,
    relatedExposureLabel,
    isDone,
    InnerComponent,
    ActionFooterComponent,
    executeAction,
    unexecuteAction,
    dismissAction,
  } = useActionCard(action, onActionUpdate);
  const [isOpen, setIsOpen] = React.useState(false);
  const [isDismissOpen, setIsDismissOpen] = React.useState(false);

  const handleDismiss = async (reason: string): Promise<void> => {
    await dismissAction(reason);
    setIsDismissOpen(false);
  };

  const handleToggle = (): void => {
    setIsOpen((prev) => {
      mixpanel.track(CLIVE_MIXPANEL_EVENTS.ACTION_EXPEND_CLICKED, { type: taskType, open: !prev });
      return !prev;
    });
  };

  return (
    <li
      className={cn(
        'animate-fadeIn flex list-none flex-col gap-12 rounded-xl border-1 border-solid border-blue-200 bg-white p-24',
        { 'border-blue-300': isOpen }
      )}
    >
      {/* Header */}
      <button
        type="button"
        onClick={handleToggle}
        className="flex w-full cursor-pointer items-center gap-16 border-0 bg-transparent p-0 text-left"
      >
        <div
          className={cn(
            'flex shrink-0 items-center justify-center rounded-lg p-8',
            isDone ? 'bg-green-100' : 'bg-pink-100'
          )}
        >
          {isDone ? INSIGHTS_ICONS.checked : INSIGHTS_ICONS.warning}
        </div>

        <span className="flex grow flex-col gap-2">
          <Text variant={Text.VARIANTS.XL} weight={Text.WEIGHTS.SEMI_BOLD} colorVariant={Text.COLOR_VARIANTS.BLACK}>
            {title}
          </Text>
          {relatedExposureLabel ? (
            <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
              {relatedExposureLabel}
            </Text>
          ) : null}
        </span>
      </button>

      {/* Body — visible only when expanded */}
      {isOpen ? (
        <div className="flex flex-col gap-12">
          {InnerComponent ? <InnerComponent action={action} /> : null}
          {ActionFooterComponent ? (
            <ActionFooterComponent
              action={action}
              onExecute={executeAction}
              onUnexecute={unexecuteAction}
              onDismiss={() => setIsDismissOpen(true)}
              isDone={isDone}
              type={taskType}
              title={title}
              subtitle={relatedExposureLabel}
            />
          ) : null}
        </div>
      ) : null}

      {isDismissOpen ? (
        <DismissWithReasonDialog
          title={title}
          subtitle={relatedExposureLabel}
          subject="recommendation"
          confirmLabel="Dismiss"
          onConfirm={handleDismiss}
          onClose={() => setIsDismissOpen(false)}
        />
      ) : null}
    </li>
  );
};
